import { useRef, useEffect, useCallback } from 'react';

/**
 * useMounted is intended for asynchronous use, and provides a getter
 * that will return true if the component is still mounted.
 *
 * This can be helpful to gate actions that should not occur after the component
 * unmounts, such as setState calls. However, if the same logic can be accomplished
 * through useEffect and it's cleanup calls, we encourage that usage instead, as it's
 * a more declarative method of coding, over the imperative querying of `isMounted`.
 */
export const useMounted = (): (() => boolean) => {
  const mountedRef = useRef<boolean>(false);
  const isMounted = useCallback(() => mountedRef.current, []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return isMounted;
};
